import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    apiReferenceV3: {
      insertApiReferenceV3: (attrs?: {
        step?: number;
        title?: string;
        method?: ApiReferenceV3Method;
        path?: string;
        description?: string;
        href?: string;
      }) => ReturnType;
      updateApiReferenceV3: (attrs: {
        step?: number;
        title?: string;
        method?: ApiReferenceV3Method;
        path?: string;
        description?: string;
        href?: string;
      }) => ReturnType;
    };
  }
}

export type ApiReferenceV3Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

export interface ApiReferenceV3Options {
  HTMLAttributes: Record<string, any>;
  view: any;
}

const ALLOWED_METHODS: ApiReferenceV3Method[] = [
  'get',
  'post',
  'put',
  'patch',
  'delete',
];

function normalizeMethod(v: unknown): ApiReferenceV3Method {
  if (typeof v !== 'string') return 'get';
  const lower = v.toLowerCase() as ApiReferenceV3Method;
  return ALLOWED_METHODS.includes(lower) ? lower : 'get';
}

function strAttr(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function intAttr(v: unknown, fallback = 1): number {
  const n = typeof v === 'number' ? v : parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
}

export const ApiReferenceV3 = Node.create<ApiReferenceV3Options>({
  name: 'apiReferenceV3',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      view: null,
    };
  },

  addAttributes() {
    return {
      step: {
        default: 1,
        parseHTML: (el) => intAttr(el.getAttribute('data-step'), 1),
        renderHTML: (attrs) => ({ 'data-step': String(intAttr(attrs.step, 1)) }),
      },
      title: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-title') ?? '',
        renderHTML: (attrs) => ({ 'data-title': strAttr(attrs.title) }),
      },
      method: {
        default: 'get' as ApiReferenceV3Method,
        parseHTML: (el) => normalizeMethod(el.getAttribute('data-method')),
        renderHTML: (attrs) => ({
          'data-method': normalizeMethod(attrs.method),
        }),
      },
      path: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-path') ?? '',
        renderHTML: (attrs) => ({ 'data-path': strAttr(attrs.path) }),
      },
      description: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-description') ?? '',
        renderHTML: (attrs) => ({
          'data-description': strAttr(attrs.description),
        }),
      },
      href: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-href') ?? '',
        renderHTML: (attrs) => ({ 'data-href': strAttr(attrs.href) }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="api-reference-v3"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': 'api-reference-v3' },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
    ];
  },

  addCommands() {
    return {
      insertApiReferenceV3:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              step: intAttr(attrs?.step, 1),
              title: strAttr(attrs?.title),
              method: normalizeMethod(attrs?.method),
              path: strAttr(attrs?.path),
              description: strAttr(attrs?.description),
              href: strAttr(attrs?.href),
            },
          }),
      updateApiReferenceV3:
        (attrs) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, {
            ...(attrs.step !== undefined && { step: intAttr(attrs.step, 1) }),
            ...(attrs.title !== undefined && { title: attrs.title }),
            ...(attrs.method !== undefined && {
              method: normalizeMethod(attrs.method),
            }),
            ...(attrs.path !== undefined && { path: attrs.path }),
            ...(attrs.description !== undefined && {
              description: attrs.description,
            }),
            ...(attrs.href !== undefined && { href: attrs.href }),
          }),
    };
  },

  addNodeView() {
    if (!this.options.view) return null;
    return ReactNodeViewRenderer(this.options.view);
  },
});
